import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentMap, getGeneration, getGenerationHistory } from './generationApi';
import { GENERATION_POLL_MS, isGenerationActive, GenerationJob, GenerationParams } from './generationTypes';

export const generationKeys = {
  all: ['admin', 'map-generations'] as const,
  history: ['admin', 'map-generations', 'history'] as const,
  current: ['admin', 'map-generations', 'current'] as const,
  detail: (id: string | null) => ['admin', 'map-generations', 'detail', id] as const,
};

const readOptions = { retry: false, refetchOnWindowFocus: false, staleTime: 0, gcTime: 0 } as const;

export function useGenerationHistory(params: GenerationParams) {
  return useQuery({ ...readOptions, queryKey: [...generationKeys.history, params], queryFn: ({ signal }) => getGenerationHistory(params, signal) });
}

export function useGenerationBlockers() {
  return useQuery({ ...readOptions, queryKey: [...generationKeys.all, 'blockers'], queryFn: async ({ signal }) => {
    const statuses = ['queued', 'running', 'recovery_required'] as const;
    const requests = statuses.map((status) => getGenerationHistory({ page: 1, per_page: 1, sort: '-created_at', status }, signal));
    const pages = await Promise.all(requests);
    return pages.flatMap((page) => page.items);
  }, refetchInterval: 10000 });
}

export function useCurrentMap() {
  return useQuery({ ...readOptions, queryKey: generationKeys.current, queryFn: ({ signal }) => getCurrentMap(signal) });
}

export function useGenerationDetail(id: string | null) {
  const client = useQueryClient();
  const queryKey = generationKeys.detail(id);
  const query = useQuery<GenerationJob, Error>({ ...readOptions, queryKey, enabled: id !== null, refetchInterval: (query) => query.state.status === 'success' && query.state.data && isGenerationActive(query.state.data) ? GENERATION_POLL_MS : false, queryFn: ({ signal }) => {
    if (id === null) throw new Error('Запуск не выбран.');
    return getGeneration(id, signal);
  } });
  const job = query.data;
  const terminalId = query.isSuccess && job && !isGenerationActive(job) ? `${job.id}:${job.status}` : null;

  useEffect(() => {
    if (terminalId === null) return;
    void client.invalidateQueries({ queryKey: generationKeys.history });
    void client.invalidateQueries({ queryKey: generationKeys.current });
    void client.invalidateQueries({ queryKey: [...generationKeys.all, 'blockers'] });
  }, [client, terminalId]);

  return query;
}
